/**
 * UserAccountTypesPage - Admin-only page to manage user account types.
 * 
 * Features:
 * - Lists all users with their account types (platform_admin, agency, business_owner)
 * - Allows admins to promote/demote users between account types
 * - Self-demotion protection for platform admins
 * - Audit log of account type changes
 * - Responsive table with mobile card view
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Shield, Users2, Building2, AlertTriangle, History, ArrowRight } from 'lucide-react';
import { Navigate } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type AccountType = 'platform_admin' | 'agency' | 'business_owner';

interface UserWithAccountType {
  out_user_id: string;
  out_account_type: AccountType;
  out_email: string;
  out_created_at: string;
}

interface AuditLogEntry {
  out_id: string;
  out_changed_by: string;
  out_changed_by_email: string | null;
  out_target_user_id: string;
  out_target_email: string | null;
  out_old_account_type: AccountType;
  out_new_account_type: AccountType;
  out_changed_at: string;
}

const ACCOUNT_TYPE_CONFIG: Record<AccountType, { label: string; icon: React.ElementType; badgeClass: string }> = {
  platform_admin: {
    label: 'Platform Admin',
    icon: Shield,
    badgeClass: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  },
  agency: {
    label: 'Agency',
    icon: Users2,
    badgeClass: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
  },
  business_owner: {
    label: 'Business Owner',
    icon: Building2,
    badgeClass: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  },
};

export default function UserAccountTypesPage() {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    userId: string;
    newType: AccountType;
    currentType: AccountType;
    email: string;
  } | null>(null);

  // Fetch users with account types
  const { data: users, isLoading, error } = useQuery({
    queryKey: ['admin-users-account-types'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('calie_admin_list_users_with_account_types');
      if (error) throw error;
      return data as UserWithAccountType[];
    },
    enabled: !authLoading && isAdmin(),
  });

  // Fetch audit logs
  const { data: auditLogs, isLoading: auditLoading, error: auditError } = useQuery({
    queryKey: ['admin-account-type-audit'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('calie_admin_get_account_type_audit', {
        _limit: 50,
      });
      if (error) throw error;
      return data as AuditLogEntry[];
    },
    enabled: !authLoading && isAdmin(),
  });

  // Mutation to update account type
  const updateAccountTypeMutation = useMutation({
    mutationFn: async ({ userId, newType }: { userId: string; newType: AccountType }) => {
      const { data, error } = await supabase.rpc('calie_admin_set_account_type', {
        _target_user_id: userId,
        _new_type: newType,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users-account-types'] });
      queryClient.invalidateQueries({ queryKey: ['admin-account-type-audit'] });
      toast.success('Account type updated successfully');
      setConfirmDialog(null);
    },
    onError: (error: Error) => {
      toast.error(`Failed to update account type: ${error.message}`);
      setConfirmDialog(null);
    },
  });

  // Redirect non-admins
  if (!authLoading && !isAdmin()) {
    return <Navigate to="/" replace />;
  }

  const handleAccountTypeChange = (userId: string, newType: AccountType, currentType: AccountType, email: string) => {
    // Self-demotion check
    if (userId === user?.id && newType !== 'platform_admin') {
      toast.error('You cannot demote yourself from platform admin');
      return;
    }

    // Confirm before changing to/from platform_admin
    if (currentType === 'platform_admin' || newType === 'platform_admin') {
      setConfirmDialog({ open: true, userId, newType, currentType, email });
    } else {
      updateAccountTypeMutation.mutate({ userId, newType });
    }
  };

  const confirmChange = () => {
    if (confirmDialog) {
      updateAccountTypeMutation.mutate({
        userId: confirmDialog.userId,
        newType: confirmDialog.newType,
      });
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  return (
    <MainLayout>
      <div className="container mx-auto py-6 px-4 max-w-6xl">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              User Account Types
            </CardTitle>
            <CardDescription>
              Manage user account types. Platform admins have full system access, agencies use their own Twilio accounts, and business owners use Calie.ai-provisioned accounts.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="users" className="w-full">
              <TabsList className="mb-4">
                <TabsTrigger value="users" className="gap-2">
                  <Users2 className="h-4 w-4" />
                  Users
                </TabsTrigger>
                <TabsTrigger value="audit" className="gap-2">
                  <History className="h-4 w-4" />
                  Audit Log
                </TabsTrigger>
              </TabsList>

              {/* Users Tab */}
              <TabsContent value="users">
                {isLoading ? (
                  <div className="space-y-3">
                    {[...Array(5)].map((_, i) => (
                      <Skeleton key={i} className="h-12 w-full" />
                    ))}
                  </div>
                ) : error ? (
                  <div className="text-center py-8 text-destructive">
                    <AlertTriangle className="h-8 w-8 mx-auto mb-2" />
                    <p>Failed to load users: {(error as Error).message}</p>
                  </div>
                ) : users && users.length > 0 ? (
                  <>
                    {/* Desktop Table */}
                    <div className="hidden md:block">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Email</TableHead>
                            <TableHead>Account Type</TableHead>
                            <TableHead>Created</TableHead>
                            <TableHead className="w-[200px]">Change Type</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                        {users.map((u) => {
                            const config = ACCOUNT_TYPE_CONFIG[u.out_account_type];
                            const Icon = config.icon;
                            const isSelf = u.out_user_id === user?.id;
                            
                            return (
                              <TableRow key={u.out_user_id}>
                                <TableCell className="font-medium">
                                  {u.out_email}
                                  {isSelf && (
                                    <Badge variant="outline" className="ml-2 text-xs">You</Badge>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Badge className={config.badgeClass}>
                                    <Icon className="h-3 w-3 mr-1" />
                                    {config.label}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-muted-foreground">
                                  {new Date(u.out_created_at).toLocaleDateString()}
                                </TableCell>
                                <TableCell>
                                  <Select
                                    value={u.out_account_type}
                                    onValueChange={(value) => 
                                      handleAccountTypeChange(u.out_user_id, value as AccountType, u.out_account_type, u.out_email)
                                    }
                                    disabled={updateAccountTypeMutation.isPending}
                                  >
                                    <SelectTrigger className="w-[180px]">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="platform_admin">Platform Admin</SelectItem>
                                      <SelectItem value="agency">Agency</SelectItem>
                                      <SelectItem value="business_owner">Business Owner</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>

                    {/* Mobile Card View */}
                    <div className="md:hidden space-y-4">
                      {users.map((u) => {
                        const config = ACCOUNT_TYPE_CONFIG[u.out_account_type];
                        const Icon = config.icon;
                        const isSelf = u.out_user_id === user?.id;
                        
                        return (
                          <Card key={u.out_user_id} className="p-4">
                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <span className="font-medium text-sm truncate max-w-[200px]">
                                  {u.out_email}
                                </span>
                                {isSelf && (
                                  <Badge variant="outline" className="text-xs">You</Badge>
                                )}
                              </div>
                              <div className="flex items-center justify-between">
                                <Badge className={config.badgeClass}>
                                  <Icon className="h-3 w-3 mr-1" />
                                  {config.label}
                                </Badge>
                                <span className="text-xs text-muted-foreground">
                                  {new Date(u.out_created_at).toLocaleDateString()}
                                </span>
                              </div>
                              <Select
                                value={u.out_account_type}
                                onValueChange={(value) => 
                                  handleAccountTypeChange(u.out_user_id, value as AccountType, u.out_account_type, u.out_email)
                                }
                                disabled={updateAccountTypeMutation.isPending}
                              >
                                <SelectTrigger className="w-full">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="platform_admin">Platform Admin</SelectItem>
                                  <SelectItem value="agency">Agency</SelectItem>
                                  <SelectItem value="business_owner">Business Owner</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </Card>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users2 className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No users found</p>
                  </div>
                )}
              </TabsContent>

              {/* Audit Log Tab */}
              <TabsContent value="audit">
                {auditLoading ? (
                  <div className="space-y-3">
                    {[...Array(5)].map((_, i) => (
                      <Skeleton key={i} className="h-12 w-full" />
                    ))}
                  </div>
                ) : auditError ? (
                  <div className="text-center py-8 text-destructive">
                    <AlertTriangle className="h-8 w-8 mx-auto mb-2" />
                    <p>Failed to load audit logs: {(auditError as Error).message}</p>
                  </div>
                ) : auditLogs && auditLogs.length > 0 ? (
                  <>
                    {/* Desktop Audit Table */}
                    <div className="hidden md:block">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Date/Time</TableHead>
                            <TableHead>Changed By</TableHead>
                            <TableHead>Target User</TableHead>
                            <TableHead>Change</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {auditLogs.map((log) => {
                            const oldConfig = ACCOUNT_TYPE_CONFIG[log.out_old_account_type];
                            const newConfig = ACCOUNT_TYPE_CONFIG[log.out_new_account_type];
                            const OldIcon = oldConfig.icon;
                            const NewIcon = newConfig.icon;

                            return (
                              <TableRow key={log.out_id}>
                                <TableCell className="text-muted-foreground whitespace-nowrap">
                                  {formatDateTime(log.out_changed_at)}
                                </TableCell>
                                <TableCell className="font-medium">
                                  {log.out_changed_by_email || 'Unknown'}
                                </TableCell>
                                <TableCell>
                                  {log.out_target_email || log.out_target_user_id}
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <Badge className={oldConfig.badgeClass}>
                                      <OldIcon className="h-3 w-3 mr-1" />
                                      {oldConfig.label}
                                    </Badge>
                                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                                    <Badge className={newConfig.badgeClass}>
                                      <NewIcon className="h-3 w-3 mr-1" />
                                      {newConfig.label}
                                    </Badge>
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>

                    {/* Mobile Audit Cards */}
                    <div className="md:hidden space-y-4">
                      {auditLogs.map((log) => {
                        const oldConfig = ACCOUNT_TYPE_CONFIG[log.out_old_account_type];
                        const newConfig = ACCOUNT_TYPE_CONFIG[log.out_new_account_type];
                        const OldIcon = oldConfig.icon;
                        const NewIcon = newConfig.icon;

                        return (
                          <Card key={log.out_id} className="p-4">
                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-muted-foreground">
                                  {formatDateTime(log.out_changed_at)}
                                </span>
                              </div>
                              <div className="space-y-1">
                                <p className="text-sm">
                                  <span className="text-muted-foreground">Changed by: </span>
                                  <span className="font-medium">{log.out_changed_by_email || 'Unknown'}</span>
                                </p>
                                <p className="text-sm">
                                  <span className="text-muted-foreground">Target: </span>
                                  <span className="font-medium">{log.out_target_email || log.out_target_user_id}</span>
                                </p>
                              </div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge className={oldConfig.badgeClass}>
                                  <OldIcon className="h-3 w-3 mr-1" />
                                  {oldConfig.label}
                                </Badge>
                                <ArrowRight className="h-4 w-4 text-muted-foreground" />
                                <Badge className={newConfig.badgeClass}>
                                  <NewIcon className="h-3 w-3 mr-1" />
                                  {newConfig.label}
                                </Badge>
                              </div>
                            </div>
                          </Card>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <History className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No account type changes recorded yet</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* Confirmation Dialog */}
      <AlertDialog open={confirmDialog?.open} onOpenChange={(open) => !open && setConfirmDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Account Type Change</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDialog && (
                <>
                  You are about to change <strong>{confirmDialog.email}</strong> from{' '}
                  <strong>{ACCOUNT_TYPE_CONFIG[confirmDialog.currentType].label}</strong> to{' '}
                  <strong>{ACCOUNT_TYPE_CONFIG[confirmDialog.newType].label}</strong>.
                  {confirmDialog.newType === 'platform_admin' && (
                    <span className="block mt-2 text-amber-600 dark:text-amber-400">
                      ⚠️ This will grant full administrative access to the platform.
                    </span>
                  )}
                  {confirmDialog.currentType === 'platform_admin' && (
                    <span className="block mt-2 text-amber-600 dark:text-amber-400">
                      ⚠️ This will revoke administrative access from this user.
                    </span>
                  )}
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmChange}>
              Confirm Change
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MainLayout>
  );
}
